import type { CodeableConcept } from '@medplum/fhirtypes';

/** Liza FHIR extension URLs. */
export const EXTENSIONS = {
  SCHEDULED_TIMING: 'https://liza.com/fhir/ext/scheduled-timing',
  TIMING_FREQUENCY: 'https://liza.com/fhir/ext/scheduled-timing#frequency',
  TIMING_PERIOD: 'https://liza.com/fhir/ext/scheduled-timing#period',
  TIMING_PERIOD_UNIT: 'https://liza.com/fhir/ext/scheduled-timing#periodUnit',
  TIMING_DAY_OF_WEEK: 'https://liza.com/fhir/ext/scheduled-timing#dayOfWeek',
  TIMING_TIME_OF_DAY: 'https://liza.com/fhir/ext/scheduled-timing#timeOfDay',
  TIMING_WHEN: 'https://liza.com/fhir/ext/scheduled-timing#when',
  TIMING_BOUNDS_START: 'https://liza.com/fhir/ext/scheduled-timing#boundsStart',
  TIMING_BOUNDS_END: 'https://liza.com/fhir/ext/scheduled-timing#boundsEnd',
  OWNER_TYPE: 'https://liza.com/fhir/ext/owner-type',
  /** Marks a care-plan template Task as "as needed" (PRN): no schedule, reported on demand. */
  AS_NEEDED: 'https://liza.health/fhir/ext/as-needed',
  /** JSON-encoded patient references attached to a message. */
  PATIENT_REFERENCES: 'https://liza.health/fhir/ext/patient-references',
  /** JSON-encoded charts data attached to a message. */
  CHARTS_DATA: 'https://liza.health/fhir/ext/charts-data',
  /** Confidence score for an AI suggested action (Task). */
  AI_SUGGESTED_ACTION_CONFIDENCE: 'https://liza.health/fhir/ext/ai-suggested-action-confidence',
  /** Correlation key (`placeholderId`) on a topic suggested-action Task, forward-referenced by a same-turn child. */
  AI_SUGGESTED_ACTION_PLACEHOLDER_ID: 'https://liza.health/fhir/ext/ai-suggested-action-placeholder-id',
  /** The `<id>` from a child suggested-action Task's `placeholder:<id>` token, naming the topic it attaches to. */
  AI_SUGGESTED_ACTION_DEPENDS_ON_PLACEHOLDER_ID:
    'https://liza.health/fhir/ext/ai-suggested-action-depends-on-placeholder-id',
  /**
   * Marks a `Communication.payload` `contentString` entry as OCR-derived text, distinguishing it
   * from original payloads. `valueString` is the source attachment's `title`, when available.
   */
  OCR_EXTRACTED_TEXT: 'https://liza.health/fhir/ext/ocr-extracted-text',
  /**
   * Suggested (unconfirmed) patient attribution for an inbound fax Communication. A complex
   * extension: `matched` (valueBoolean), plus `patient` (valueReference) and `grade` (valueCode:
   * certain | probable | possible) when a match was found. Deliberately not `Communication.subject`
   * — the match is probabilistic and must not read as a confirmed clinical link; its presence
   * (match or no-match) marks attribution as already attempted.
   */
  SUGGESTED_PATIENT_ATTRIBUTION: 'https://liza.health/fhir/ext/suggested-patient-attribution',
  /**
   * Free-text justification for a worklist `Flag` (why a patient was pinned or dismissed). FHIR R4
   * `Flag` has no reason element, so the reason is carried here as a `valueString`. Clinician-entered
   * for manual flags; for auto-escalation it is the model's decision reason (prefixed with its category).
   */
  WORKLIST_FLAG_REASON: 'https://liza.health/fhir/ext/worklist-flag-reason',
  /**
   * The acting staff `Practitioner` reference for an `allowOverride` booking (#9291) — distinct from
   * `Appointment.participant`, which records the *treating* clinician, not who performed the override.
   */
  APPOINTMENT_OVERRIDDEN_BY: 'https://liza.health/fhir/ext/appointment-overridden-by',
  /**
   * Free-text content of a patient's staff-only private note, carried on a `Flag` (see
   * `getPatientPrivateNoteText` in `./patientPrivateNote`). FHIR R4 `Flag` has no free-text element,
   * same rationale as {@link EXTENSIONS.WORKLIST_FLAG_REASON}.
   */
  PATIENT_PRIVATE_NOTE: 'https://liza.health/fhir/ext/patient-private-note',
  /**
   * Who made the booking, as a `Practitioner` or `Patient` reference (#9416) — a patient booking their
   * own visit, or the staff member who booked it for them.
   *
   * Distinct from every other actor an `Appointment` names: `participant` records who is *attending*,
   * and {@link APPOINTMENT_OVERRIDDEN_BY} who waived the availability check. Not derivable from
   * `meta.author` either, since every booking is written by the admin client rather than the caller.
   *
   * Absent on a cal.com-owned booking, which carries no booker identity we can map to a resource.
   */
  APPOINTMENT_BOOKED_BY: 'https://liza.health/fhir/ext/appointment-booked-by',
  /**
   * The clinician's schedule color, a `valueString` hex from `PRACTITIONER_APPOINTMENT_COLORS` (#9416)
   * — what the Appointments grid fills their blocks with.
   *
   * Stored rather than derived from the id, so a clinician keeps one color across every view and
   * session, and so the picker can see which colors their practice holds. Assigned when their
   * availability is saved; absent until then.
   */
  PRACTITIONER_APPOINTMENT_COLOR: 'https://liza.health/fhir/ext/practitioner-appointment-color',
} as const;

export type ExtensionValueType = 'string' | 'boolean' | 'decimal';

/**
 * The `value[x]` type each simple Liza extension carries. This is the one fact about an extension
 * that belongs to the extension rather than to the profiles that use it, so profiles read it from here
 * and cannot disagree with the writers. Complex extensions (`SCHEDULED_TIMING`) carry no value and
 * are not listed. Add an entry when a profile starts constraining an extension.
 */
export const EXTENSION_VALUE_TYPES = {
  [EXTENSIONS.OWNER_TYPE]: 'string',
  [EXTENSIONS.AS_NEEDED]: 'boolean',
  [EXTENSIONS.AI_SUGGESTED_ACTION_CONFIDENCE]: 'decimal',
} as const satisfies Partial<Record<(typeof EXTENSIONS)[keyof typeof EXTENSIONS], ExtensionValueType>>;

/**
 * Marks a chat `Communication` as machine-authored (`valueBoolean: true`) even though its `sender` is
 * a real `Practitioner` — the automatic care-plan bootstrap sends its trigger message as the
 * patient's primary care team member, because `provider` is the sender type the whole patientpedia
 * pipeline already speaks. Without this the message is indistinguishable in the data from one that
 * provider typed, so a reader has no way to label it as automated.
 */
export const MACHINE_AUTHORED_MESSAGE_EXTENSION = 'https://liza.health/fhir/ext/machine-authored-message';

/**
 * Standard HL7 quantity-translation extension: an alternative representation of the same
 * physical quantity in a different unit. We annotate ingested metric vitals with their
 * imperial equivalent using this.
 * http://hl7.org/fhir/extensions/StructureDefinition-extension-quantity-translation.html
 */
export const QUANTITY_TRANSLATION_EXTENSION = 'http://hl7.org/fhir/StructureDefinition/extension-quantity-translation';

/** FHIR when code → executionPeriod time ranges. */
export const TIME_PERIODS: Record<string, { start: string; end: string }> = {
  'MORN.early': { start: '05:00', end: '08:00' },
  MORN: { start: '06:00', end: '12:00' },
  'MORN.late': { start: '09:00', end: '12:00' },
  'AFT.early': { start: '12:00', end: '15:00' },
  AFT: { start: '12:00', end: '18:00' },
  'AFT.late': { start: '15:00', end: '18:00' },
  'EVE.early': { start: '17:00', end: '19:00' },
  EVE: { start: '17:00', end: '21:00' },
  NIGHT: { start: '21:00', end: '06:00' },
};

/** Liza care plan category coding system. */
export const CARE_PLAN_CATEGORY_SYSTEM = 'http://liza.health/fhir/care-plan-category';

/** Coding system on a suggested-action Task's `code.coding[]`; the code is the tool name. */
export const AI_ACTION_CODE_SYSTEM = 'https://liza.health/fhir/code/ai-action';

/**
 * The bare (non-URL) system on the `Task.input` entry that carries a care-plan item's category as
 * `valueString`. Legacy shape kept for reading; the `task-category` CodeSystem replaces it.
 */
export const LEGACY_TASK_CATEGORY_SYSTEM = 'liza-task-category';

/**
 * `Task.code.coding[].system` carrying a task's clinical domain (`TASK_CATEGORY_CODES`). Written
 * alongside the legacy `input` marker, which readers still use; searchable as `code=<system>|food`.
 * The CodeSystem itself is deployed by `medplum-config`.
 */
export const TASK_CATEGORY_SYSTEM = 'https://liza.health/fhir/CodeSystem/task-category';

/**
 * `Task.code.coding[].system` carrying a care-plan item's kind (`CARE_PLAN_ITEM_KINDS`), the value
 * `getCarePlanItemKind` otherwise derives at read time. Searchable as `code=<system>|medication`.
 */
export const CARE_PLAN_ITEM_KIND_SYSTEM = 'https://liza.health/fhir/CodeSystem/care-plan-item-kind';

/** Standard terminology system URIs, so no file spells `http://loinc.org` for itself. */
export const CODE_SYSTEMS = {
  LOINC: 'http://loinc.org',
  SNOMED: 'http://snomed.info/sct',
  UCUM: 'http://unitsofmeasure.org',
  ICD10_CM: 'http://hl7.org/fhir/sid/icd-10-cm',
} as const;

/**
 * Medplum's fax-direction category CodeSystem, stamped on eFax `Communication` resources
 * (`inbound` on faxes created by `$receive-efax`, `outbound` on sent faxes).
 */
export const FAX_DIRECTION_SYSTEM = 'http://medplum.com/fhir/CodeSystem/fax-direction';

/**
 * eFax extension carrying the sender's fax number (`valueString`) on inbound fax
 * `Communication` resources created by `$receive-efax`. The raw value is 11 digits
 * with a leading `1` and no `+` prefix, e.g. `"15034367197"`.
 */
export const EFAX_ORIGINATING_FAX_NUMBER_EXTENSION_URL = 'https://efax.com/originating-fax-number';

/**
 * Liza document-category CodeSystem, stamped on `DocumentReference.category`. Categories mark
 * how a document entered the system — deliberately distinct from the `document-type` system
 * carried on `type`, which stays free to describe what a document *is*.
 */
export const DOCUMENT_CATEGORY_SYSTEM = 'https://liza.health/fhir/CodeSystem/document-category';

/**
 * Liza chat-surface CodeSystem, stamped on `Communication.medium` of human-authored chat
 * messages. Records which app surface a message came from so the async patientpedia job can
 * key its capabilities prompt on it; LLM-authored messages carry no medium.
 */
export const CHAT_SURFACE_SYSTEM = 'https://liza.health/fhir/CodeSystem/chat-surface';

/**
 * DocumentReference category marking a document uploaded by a user — the discriminator the
 * Documents list queries by. The code says *user*, not *practitioner*: patient uploads reuse
 * it with a Patient `author`.
 */
export const USER_UPLOAD_DOCUMENT_CATEGORY_CODING = {
  system: DOCUMENT_CATEGORY_SYSTEM,
  code: 'user-upload',
} as const;

/** FHIR token search value (`system|code`) for scoping a search to user-uploaded documents. */
export const USER_UPLOAD_DOCUMENT_CATEGORY_SEARCH_TOKEN = `${USER_UPLOAD_DOCUMENT_CATEGORY_CODING.system}|${USER_UPLOAD_DOCUMENT_CATEGORY_CODING.code}`;

/**
 * DocumentReference category marking a file attached to a chat message, referenced from that
 * message's `Communication.payload`. Deliberately not {@link USER_UPLOAD_DOCUMENT_CATEGORY_CODING}:
 * the patient record's Documents tab queries that category, and chat uploads are not part of the
 * record this pass.
 */
export const CHAT_ATTACHMENT_DOCUMENT_CATEGORY_CODING = {
  system: DOCUMENT_CATEGORY_SYSTEM,
  code: 'chat-attachment',
} as const;

/** FHIR token search value (`system|code`) for scoping a search to chat attachments. */
export const CHAT_ATTACHMENT_DOCUMENT_CATEGORY_SEARCH_TOKEN = `${CHAT_ATTACHMENT_DOCUMENT_CATEGORY_CODING.system}|${CHAT_ATTACHMENT_DOCUMENT_CATEGORY_CODING.code}`;

/**
 * DocumentReference type marking the raw, provider-pasted visit transcription. Deliberately
 * distinct from the LOINC "Progress note" (11506-3) used for human-authored notes and the
 * AI-generated encounter summary, so downstream steps can tell the raw transcript apart from
 * its summary even though both hang off the same Encounter.
 */
export const TRANSCRIPTION_NOTE_CODING = {
  system: 'https://liza.health/fhir/CodeSystem/document-type',
  code: 'visit-transcription',
  display: 'Visit transcription',
} as const;

/**
 * LOINC "Progress note" coding. Marks both human-authored encounter notes and the
 * AI-generated encounter summary — deliberately distinct from {@link TRANSCRIPTION_NOTE_CODING}
 * so the raw transcript can be told apart from notes hanging off the same Encounter.
 */
export const PROGRESS_NOTE_CODING = {
  system: 'http://loinc.org',
  code: '11506-3',
  display: 'Progress note',
} as const;

/**
 * `DocumentReference.content.format` codings that tell a consent document's content entries apart:
 * the full legal text the patient signs and the plain-language bullets summarizing it in the
 * consent preamble, each in HTML (web) and Markdown (native) renditions. All four are authored per
 * language on the same document, so the summary can't drift from the text it summarizes.
 *
 * A content entry with no `format` is the HTML full text — documents deployed before summary
 * bullets existed carry no coding, and treating them as the body keeps them readable between the
 * code deploy and the next `medplum-config` deploy.
 */
export const CONSENT_DOCUMENT_FORMAT_SYSTEM = 'https://liza.health/fhir/CodeSystem/consent-document-format';

export const CONSENT_DOCUMENT_FORMAT = {
  fullText: {
    system: CONSENT_DOCUMENT_FORMAT_SYSTEM,
    code: 'full-text',
    display: 'Full consent text',
  },
  fullTextMarkdown: {
    system: CONSENT_DOCUMENT_FORMAT_SYSTEM,
    code: 'full-text-markdown',
    display: 'Full consent text (Markdown)',
  },
  summaryBullets: {
    system: CONSENT_DOCUMENT_FORMAT_SYSTEM,
    code: 'summary-bullets',
    display: 'Plain-language summary bullets',
  },
  summaryBulletsMarkdown: {
    system: CONSENT_DOCUMENT_FORMAT_SYSTEM,
    code: 'summary-bullets-markdown',
    display: 'Plain-language summary bullets (Markdown)',
  },
} as const;

/** FHIR extension URLs for the onboarding configuration stored on Organization resources. */
export const ONBOARDING_EXTENSION_URLS = {
  videoReference: 'https://liza.health/fhir/extension/onboarding-video-reference',
  doctorName: 'https://liza.health/fhir/extension/onboarding-doctor-name',
  doctorLabel: 'https://liza.health/fhir/extension/onboarding-doctor-label',
  doctorMessage: 'https://liza.health/fhir/extension/onboarding-doctor-message',
  /**
   * Repeating: one entry per consent linkId the practice requires. Absent means the practice
   * requires {@link DEFAULT_ONBOARDING_CONSENT_LINK_IDS}, so adding a consent to the questionnaire
   * doesn't change what any existing practice asks for.
   */
  requiredConsents: 'https://liza.health/fhir/extension/onboarding-required-consents',
} as const;

/**
 * Every consent the `onboarding-v2` questionnaire can serve. The questionnaire carries the union of
 * these; which ones a given practice requires comes from its `requiredConsents` extension.
 */
export const ONBOARDING_CONSENT_LINK_IDS = [
  'consent-care-relationship',
  'consent-hipaa-auth',
  'consent-ephi',
  'consent-dpc-membership',
] as const;

export type OnboardingConsentLinkId = (typeof ONBOARDING_CONSENT_LINK_IDS)[number];

/**
 * What a practice requires when it has no `requiredConsents` extension — the three consents every
 * practice asked for before the set became configurable.
 */
export const DEFAULT_ONBOARDING_CONSENT_LINK_IDS: readonly OnboardingConsentLinkId[] = [
  'consent-care-relationship',
  'consent-hipaa-auth',
  'consent-ephi',
];

/**
 * The `Consent.category` code recorded for a signed consent, derived from its questionnaire linkId.
 * Both the patient-facing submit path and the seeding helpers go through this, since re-consent
 * detection compares a practice's required linkIds against the categories actually granted — a
 * second derivation that drifted would read as "this patient never consented".
 */
export const onboardingConsentCategoryCode = (linkId: string): string => linkId.replace(/^consent-/, '');

/**
 * The `onboarding-v2` questionnaire topic that carries the patient's signed consents.
 *
 * Shared across apps because "has the patient consented?" is keyed on this topic's status rather
 * than the questionnaire's overall status — completing onboarding is not proof of consent, since the
 * flow also completes when the consent topic was *skipped* (recorded as `asked-declined`). A drift
 * between the consumers (patient-native's auth gate, patient-api's consent-time Metriport sync, the
 * consent seeding in `@liza/services`) would either block every patient or silently admit an
 * unconsented one, so there is exactly one definition.
 */
export const ONBOARDING_CONSENT_TOPIC_LINK_ID = 'consent';

/**
 * The group inside the consent topic that holds one item per consent document. Shared because both
 * the server-side per-practice filtering and the clients that read the consents out of
 * `TopicProgress.questionnaireItem` have to agree on where they live.
 */
export const ONBOARDING_CONSENT_GROUP_LINK_ID = 'consent-informational';

/**
 * HL7 v3 ParticipationType, the value set `Appointment.participant.type` is bound to. Used to say
 * which clinician on a multi-clinician visit is the one whose Schedule the visit was booked into.
 */
export const PARTICIPATION_TYPE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType';

/** The `PPRF`/`SPRF` codes, for reading a participant's role back off a stored resource. */
export const PERFORMER_PARTICIPANT_CODES = { primary: 'PPRF', secondary: 'SPRF' } as const;

/**
 * `Appointment.participant.type` for the treating clinician — the one whose `Schedule` the booking
 * validated against and whose `Slot` the visit occupies. Exactly one participant carries it.
 */
export const PRIMARY_PERFORMER_PARTICIPANT_TYPE: CodeableConcept = {
  coding: [
    { system: PARTICIPATION_TYPE_SYSTEM, code: PERFORMER_PARTICIPANT_CODES.primary, display: 'primary performer' },
  ],
};

/**
 * `Appointment.participant.type` for a clinician attending a visit booked into someone else's
 * Schedule — a second practitioner, an interpreter, a supervising clinician.
 */
export const SECONDARY_PERFORMER_PARTICIPANT_TYPE: CodeableConcept = {
  coding: [
    { system: PARTICIPATION_TYPE_SYSTEM, code: PERFORMER_PARTICIPANT_CODES.secondary, display: 'secondary performer' },
  ],
};
